/**
 * Feature Flags Constants
 * Centralized configuration for LaunchDarkly feature flags.
 * See config/feature-flags/README.md for usage guide.
 */

/**
 * LaunchDarkly flag keys
 * These match the exact flag keys in LaunchDarkly dashboard
 */
export const LD_FLAG_KEYS = {
  /** Use FPS v2 API for payroll automation service */
  USE_V2_API_FOR_AUTOMATION:
    'enable-funding-and-payments-fps-cp-use-fps-v2-api-payroll-automation-service',
  /** Enable diagnostic page access toggle */
  ENABLE_DIAGNOSTIC_PAGE: 'enable-fps-web-v2-diagnostic-page-access',
} as const;

/**
 * Configuration for a single feature flag
 * All feature flags are boolean-only (ON/OFF toggles)
 */
export interface FeatureFlagConfig {
  /** Domain in the FeatureFlags interface (e.g., 'payroll', 'forms') */
  domain: string;
  /** Property name within the domain (e.g., 'useV2ApiForAutomation') */
  property: string;
  /** Default value when LaunchDarkly is unavailable (boolean only) */
  defaultValue: boolean;
  /** Human-readable description of the flag's purpose */
  description?: string;
}

/**
 * Feature flags configuration map (single source of truth)
 * Domain and property must match FeatureFlags interface
 */
export const FEATURE_FLAGS_CONFIG = {
  [LD_FLAG_KEYS.USE_V2_API_FOR_AUTOMATION]: {
    domain: 'payroll',
    property: 'useV2ApiForAutomation',
    defaultValue: false,
    description: 'Use FPS v2 API for payroll automation service',
  },
  [LD_FLAG_KEYS.ENABLE_DIAGNOSTIC_PAGE]: {
    domain: 'diagnostics',
    property: 'enableDiagnosticPageAccess',
    defaultValue: false,
    description:
      'Enable diagnostic page access for users with diagnostic permission',
  },
  // Add more flags here as needed:
  // [LD_FLAG_KEYS.ANOTHER_FLAG]: {
  //   domain: 'forms',
  //   property: 'enableNewEditor',
  //   defaultValue: false,
  //   description: 'Enable new form editor'
  // }
} as const satisfies Record<string, FeatureFlagConfig>;

/**
 * Type-safe helper to get all LaunchDarkly flag keys
 */
export type LaunchDarklyFlagKey = keyof typeof FEATURE_FLAGS_CONFIG;

/**
 * Get all LaunchDarkly flag keys as an array
 */
export function getAllFlagKeys(): readonly LaunchDarklyFlagKey[] {
  const keys = Object.keys(FEATURE_FLAGS_CONFIG) as LaunchDarklyFlagKey[];
  return Object.freeze(keys);
}

/**
 * Get configuration for a specific flag
 */
export function getFlagConfig(key: LaunchDarklyFlagKey): FeatureFlagConfig {
  return FEATURE_FLAGS_CONFIG[key];
}

/**
 * Type guard to check if a string is a valid flag key
 */
export function isValidFlagKey(key: string): key is LaunchDarklyFlagKey {
  return key in FEATURE_FLAGS_CONFIG;
}

/**
 * Build default feature flags structure from configuration
 * All domains/properties auto-generated from FEATURE_FLAGS_CONFIG
 */
export function buildDefaultFeaturesFromConfig(): Record<
  string,
  Record<string, unknown>
> {
  const defaults: Record<string, Record<string, unknown>> = {};

  for (const key of getAllFlagKeys()) {
    const config = getFlagConfig(key);
    const { domain, property, defaultValue } = config;

    // Auto-create domain if it doesn't exist
    if (!defaults[domain]) {
      defaults[domain] = {};
    }

    // Set default value for property
    defaults[domain][property] = defaultValue;
  }

  return defaults;
}
